/**
 * ScenePanel - Interface pour le mode Scene
 *
 * Permet de:
 * - Basculer entre mode Pattern, Scene et Song
 * - Gérer les scenes (créer, supprimer, dupliquer, renommer)
 * - Assigner des patterns différents par instrument dans chaque scene
 * - Muter des instruments par scene
 * - Lancer une scene d'un clic
 */

export class ScenePanel {
    constructor(store) {
        this.store = store;
        this.container = null;
        this.editingSceneId = null; // Scene en cours d'édition
        this.isCollapsed = localStorage.getItem('arrangement_collapsed') === 'true';

        // Labels lisibles pour les instruments
        this.instrumentLabels = {
            'tb303_1': 'TB-303 #1',
            'tb303_2': 'TB-303 #2',
            'tr808': 'TR-808',
            'tr909': 'TR-909',
            'minimoog': 'Minimoog',
            'ms20': 'MS-20',
            'jupiter8': 'Jupiter-8',
            'juno106': 'Juno-106',
            'sh101': 'SH-101',
            'arp2600': 'ARP 2600',
            'theremin': 'Theremin',
            'minimod': 'MiniMod'
        };

        // S'abonner aux changements de scene
        this.unsubscribeScene = this.store.onSceneChange(() => {
            this.updateDisplay();
        });

        // S'abonner aux changements de pattern pour mettre à jour l'affichage
        this.unsubscribePattern = this.store.onPatternChange(() => {
            this.updateDisplay();
        });
    }

    render(container) {
        if (typeof container === 'string') {
            this.container = document.getElementById(container);
        } else {
            this.container = container;
        }

        if (!this.container) return;

        this.updateDisplay();
    }

    updateDisplay() {
        if (!this.container) return;

        const scenes = this.store.getScenes();
        const sceneIds = Object.keys(scenes);
        const currentSceneId = this.store.getCurrentSceneId();
        const sceneMode = this.store.isSceneMode();
        const songMode = this.store.isSongMode();
        const bankIds = this.store.getPatternBankIds();

        // Déterminer le mode actif
        const activeMode = songMode ? 'song' : (sceneMode ? 'scene' : 'pattern');

        // En mode Song, toujours synchroniser avec la scène en cours de lecture
        if (songMode && currentSceneId) {
            this.editingSceneId = currentSceneId;
        }

        // Scene à éditer (courante ou première)
        const editId = this.editingSceneId || currentSceneId || sceneIds[0];
        const editScene = scenes[editId];

        this.container.innerHTML = `
            <div class="machine arrangement-machine ${this.isCollapsed ? 'collapsed' : ''}" id="arrangement_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="arrangement_collapse" title="Réduire/Agrandir">${this.isCollapsed ? '▶' : '▼'}</button>
                    ARRANGEMENT <span class="brand">Scenes & Song</span>
                    <div class="mode-toggle">
                        <button class="mode-btn ${activeMode === 'pattern' ? 'active' : ''}" data-mode="pattern" title="Mode Pattern - Tous les instruments jouent le même pattern">PATTERN</button>
                        <button class="mode-btn ${activeMode === 'scene' ? 'active' : ''}" data-mode="scene" title="Mode Scene - Chaque instrument peut jouer un pattern différent">SCENE</button>
                        <button class="mode-btn ${activeMode === 'song' ? 'active' : ''}" data-mode="song" title="Mode Song - Enchaînement automatique des scenes">SONG</button>
                    </div>
                </div>
                <div class="machine-content ${this.isCollapsed ? 'hidden' : ''}" id="arrangement_content">
                    <div class="scene-panel ${sceneMode ? 'scene-active' : ''} ${songMode ? 'song-active' : ''}">
                        <div class="scene-content ${sceneMode || songMode ? 'enabled' : 'disabled'}">
                    <div class="scene-list">
                        <div class="scene-list-header">
                            <span>SCENES</span>
                            <button class="scene-add-btn" id="addSceneBtn" title="Créer une nouvelle scene">+</button>
                        </div>
                        <div class="scene-items">
                            ${sceneIds.map(id => {
                                const scene = scenes[id];
                                const isCurrent = id === currentSceneId;
                                const isEditing = id === editId;
                                return `
                                    <div class="scene-item ${isCurrent ? 'current' : ''} ${isEditing ? 'editing' : ''}" data-scene-id="${id}">
                                        <span class="scene-name" data-scene-id="${id}">${scene.name}</span>
                                        <div class="scene-item-buttons">
                                            <button class="scene-launch-btn ${isCurrent ? 'active' : ''}" data-scene-id="${id}" title="Lancer cette scene">▶</button>
                                            <div class="scene-item-actions">
                                                <button class="scene-action-btn duplicate" data-scene-id="${id}" title="Dupliquer">⧉</button>
                                                <button class="scene-action-btn delete" data-scene-id="${id}" title="Supprimer" ${sceneIds.length <= 1 ? 'disabled' : ''}>✕</button>
                                            </div>
                                        </div>
                                    </div>
                                `;
                            }).join('')}
                        </div>
                    </div>

                    <div class="scene-editor">
                        ${editScene ? `
                            <div class="scene-editor-header">
                                <input type="text" class="scene-name-input" id="sceneNameInput" value="${editScene.name}" data-scene-id="${editId}" />
                            </div>
                            <div class="scene-instruments">
                                <div class="scene-instruments-header">
                                    <span class="col-instrument">Instrument</span>
                                    <span class="col-pattern">Pattern</span>
                                    <span class="col-mute">Mute</span>
                                </div>
                                ${this.store.instrumentIds.map(instId => {
                                    const instData = editScene.instruments[instId] || { pattern: 'A', muted: false };
                                    return `
                                        <div class="scene-instrument-row">
                                            <span class="instrument-label">${this.instrumentLabels[instId] || instId}</span>
                                            <select class="pattern-select" data-scene-id="${editId}" data-instrument-id="${instId}">
                                                ${bankIds.map(bankId => `
                                                    <option value="${bankId}" ${instData.pattern === bankId ? 'selected' : ''}>${bankId}</option>
                                                `).join('')}
                                            </select>
                                            <label class="mute-checkbox">
                                                <input type="checkbox" data-scene-id="${editId}" data-instrument-id="${instId}" ${instData.muted ? 'checked' : ''} />
                                                <span class="mute-indicator"></span>
                                            </label>
                                        </div>
                                    `;
                                }).join('')}
                            </div>
                        ` : '<div class="no-scene">Aucune scene sélectionnée</div>'}
                    </div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        this.setupEventListeners();
    }

    setupEventListeners() {
        if (!this.container) return;

        // Collapse button
        const collapseBtn = this.container.querySelector('#arrangement_collapse');
        if (collapseBtn) {
            collapseBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleCollapse();
            });
        }

        // Mode toggle buttons
        this.container.querySelectorAll('.mode-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.dataset.mode;
                if (mode === 'pattern') {
                    this.store.setSceneMode(false);
                    this.store.setSongMode(false);
                } else if (mode === 'scene') {
                    this.store.setSongMode(false);
                    this.store.setSceneMode(true);
                } else if (mode === 'song') {
                    this.store.setSceneMode(true); // Song mode utilise aussi les scenes
                    this.store.setSongMode(true);
                }
            });
        });

        // Add scene button
        const addBtn = this.container.querySelector('#addSceneBtn');
        if (addBtn) {
            addBtn.addEventListener('click', () => {
                const sceneIds = Object.keys(this.store.getScenes());
                const newName = `Scene ${sceneIds.length + 1}`;
                const newId = this.store.createScene(newName);
                this.editingSceneId = newId;
                this.updateDisplay();
            });
        }

        // Scene launch buttons
        this.container.querySelectorAll('.scene-launch-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const sceneId = btn.dataset.sceneId;

                // Mettre à jour l'édition pour rafraîchir l'éditeur
                this.editingSceneId = sceneId;

                this.store.launchScene(sceneId);

                // Activer le mode scene si pas déjà actif
                if (!this.store.isSceneMode() && !this.store.isSongMode()) {
                    this.store.setSceneMode(true);
                }
            });
        });

        // Scene items click (éditer et lancer en mode Scene/Song)
        this.container.querySelectorAll('.scene-item').forEach(item => {
            item.addEventListener('click', (e) => {
                // Ignorer si on clique sur un bouton
                if (e.target.closest('button')) return;

                const sceneId = item.dataset.sceneId;
                this.editingSceneId = sceneId;

                // En mode Scene ou Song, lancer aussi la scene
                if (this.store.isSceneMode() || this.store.isSongMode()) {
                    this.store.launchScene(sceneId);
                }

                this.updateDisplay();
            });
        });

        // Scene name double-click to edit
        this.container.querySelectorAll('.scene-name').forEach(span => {
            span.addEventListener('dblclick', (e) => {
                const sceneId = span.dataset.sceneId;
                this.editingSceneId = sceneId;
                this.updateDisplay();
                // Focus sur l'input après render
                setTimeout(() => {
                    const input = this.container.querySelector('#sceneNameInput');
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }, 10);
            });
        });

        // Scene name input
        const nameInput = this.container.querySelector('#sceneNameInput');
        if (nameInput) {
            nameInput.addEventListener('change', (e) => {
                const sceneId = e.target.dataset.sceneId;
                this.store.renameScene(sceneId, e.target.value);
            });
            nameInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.target.blur();
                }
            });
        }

        // Duplicate buttons
        this.container.querySelectorAll('.scene-action-btn.duplicate').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const sceneId = btn.dataset.sceneId;
                const newId = this.store.duplicateScene(sceneId);
                if (newId) {
                    this.editingSceneId = newId;
                }
            });
        });

        // Delete buttons
        this.container.querySelectorAll('.scene-action-btn.delete').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const sceneId = btn.dataset.sceneId;
                this.store.deleteScene(sceneId);
                // Reset editing scene si elle a été supprimée
                if (this.editingSceneId === sceneId) {
                    this.editingSceneId = null;
                }
            });
        });

        // Pattern selects in editor
        this.container.querySelectorAll('.scene-instruments .pattern-select').forEach(select => {
            select.addEventListener('change', (e) => {
                const sceneId = e.target.dataset.sceneId;
                const instrumentId = e.target.dataset.instrumentId;
                const pattern = e.target.value;
                this.store.setSceneInstrumentPattern(sceneId, instrumentId, pattern);
            });
        });

        // Mute checkboxes in editor
        this.container.querySelectorAll('.scene-instruments .mute-checkbox input').forEach(checkbox => {
            checkbox.addEventListener('change', (e) => {
                const sceneId = e.target.dataset.sceneId;
                const instrumentId = e.target.dataset.instrumentId;
                const muted = e.target.checked;
                this.store.setSceneInstrumentMuted(sceneId, instrumentId, muted);
            });
        });
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('arrangement_collapsed', this.isCollapsed);

        const machine = this.container.querySelector('#arrangement_machine');
        const content = this.container.querySelector('#arrangement_content');
        const btn = this.container.querySelector('#arrangement_collapse');

        if (this.isCollapsed) {
            machine.classList.add('collapsed');
            content.classList.add('hidden');
            btn.textContent = '▶';
        } else {
            machine.classList.remove('collapsed');
            content.classList.remove('hidden');
            btn.textContent = '▼';
        }
    }

    destroy() {
        if (this.unsubscribeScene) {
            this.unsubscribeScene();
        }
        if (this.unsubscribePattern) {
            this.unsubscribePattern();
        }
    }
}
